/**
 * Read a JSON Web Token without verifying it.
 *
 * A JWT is three base64url segments joined by dots: a header, a payload and a signature.
 * Anyone can read the first two; that is what this does. Nothing here checks a signature
 * or trusts a claim, and the functions say so by name: they decode, they do not verify.
 * Time-dependent judgements (expired, not yet valid) take the current time as an
 * argument so that the caller reads the clock, not this module.
 */

import {
  DataError,
  type DataMap,
  type DataValue,
  isMap,
  RawNumber,
  stringifyJson,
} from "@/lib/tool-kit/latex-dev/data-model";
import { parseJson } from "@/lib/tool-kit/latex-dev/json-parse";

export type JwtErrorCode =
  | "empty"
  | "segments"
  | "characters"
  | "badLength"
  | "notUtf8"
  | "notJson"
  | "notObject";

export type JwtWarning =
  | "algNone"
  | "algMissing"
  | "headerUrl"
  | "noExpiry"
  | "expBeforeIat"
  | "nbfAfterExp"
  | "milliseconds"
  | "notANumber"
  | "padding"
  | "standardBase64"
  | "prefixRemoved"
  | "signatureSize"
  | "payloadNotJson"
  | "encrypted"
  | "unsecured"
  | "critical";

export interface JwtError {
  code: JwtErrorCode;
  /** Which segment: "header", "payload", "signature" or "" for the token as a whole. */
  segment: string;
  /** Position inside the segment's JSON, when the JSON was the problem. */
  line: number;
  column: number;
  /** The number of dot-separated segments found. */
  count: number;
}

export interface Claim {
  name: string;
  value: DataValue;
  /** The value written as compact JSON. */
  json: string;
}

export interface TimeClaim {
  name: "exp" | "nbf" | "iat";
  /** Seconds since 1970-01-01 UTC, as written. */
  seconds: number;
}

export interface DecodedJwt {
  /** Five segments: only the header can be read. */
  encrypted: boolean;
  headerJson: string;
  header: DataMap;
  payloadJson: string;
  payload: DataMap | null;
  /** The payload as text, when it is not a JSON object. */
  payloadText: string;
  signature: string;
  signatureBytes: number;
  algorithm: string;
  headerClaims: Claim[];
  registered: Claim[];
  other: Claim[];
  times: TimeClaim[];
  warnings: JwtWarning[];
}

export type JwtResult =
  | { ok: true; jwt: DecodedJwt }
  | { ok: false; error: JwtError };

const REGISTERED = ["iss", "sub", "aud", "exp", "nbf", "iat", "jti"];
const TIME_NAMES = ["exp", "nbf", "iat"] as const;
const RE_BEARER = /^bearer\s+/i;
const RE_WHITESPACE = /\s+/g;
const RE_QUOTES = /^["'`]+|["'`]+$/g;
const RE_BASE64URL = /^[A-Za-z0-9_-]*$/;
const RE_STANDARD = /[+/]/;
const RE_PADDING = /=+$/;

/** Bytes a signature should have for algorithms whose size is fixed. */
const SIGNATURE_BYTES: Readonly<Record<string, number>> = {
  HS256: 32,
  HS384: 48,
  HS512: 64,
  ES256: 64,
  ES256K: 64,
  ES384: 96,
  ES512: 132,
  EdDSA: 64,
};

// ---------------------------------------------------------------------------
// Base64url
// ---------------------------------------------------------------------------

const ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const LOOKUP: Readonly<Record<string, number>> = Object.fromEntries(
  [...ALPHABET].map((c, i) => [c, i])
);

/** Decode base64url (standard base64 characters are accepted too). Null when impossible. */
export function decodeBase64Url(segment: string): Uint8Array | null {
  const text = segment
    .replace(RE_PADDING, "")
    .replaceAll("+", "-")
    .replaceAll("/", "_");
  if (text.length % 4 === 1) {
    return null;
  }
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 4) {
    const chunk = text.slice(i, i + 4);
    let value = 0;
    for (const c of chunk) {
      const digit = LOOKUP[c];
      if (digit === undefined) {
        return null;
      }
      value = value * 64 + digit;
    }
    // Pad a short final chunk to 24 bits, then take the bytes it really holds.
    value *= 64 ** (4 - chunk.length);
    const bytes = [
      Math.floor(value / 65_536),
      Math.floor(value / 256) % 256,
      value % 256,
    ];
    out.push(...bytes.slice(0, Math.floor((chunk.length * 6) / 8)));
  }
  return Uint8Array.from(out);
}

function utf8(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------

function fail(
  code: JwtErrorCode,
  segment = "",
  count = 0,
  line = 0,
  column = 0
): JwtResult {
  return { ok: false, error: { code, segment, line, column, count } };
}

function toClaims(map: DataMap, skip: ReadonlySet<string>): Claim[] {
  const claims: Claim[] = [];
  for (const [name, value] of map) {
    if (!skip.has(name)) {
      claims.push({
        name,
        value,
        json: stringifyJson(value, "compact", 1_000_000),
      });
    }
  }
  return claims;
}

function numericSeconds(value: DataValue | undefined): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (value instanceof RawNumber) {
    const n = Number(value.text);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function readTimes(payload: DataMap, warnings: Set<JwtWarning>): TimeClaim[] {
  const times: TimeClaim[] = [];
  for (const name of TIME_NAMES) {
    if (!payload.has(name)) {
      continue;
    }
    const seconds = numericSeconds(payload.get(name));
    if (seconds === null) {
      warnings.add("notANumber");
    } else {
      times.push({ name, seconds });
      if (seconds > 100_000_000_000) {
        warnings.add("milliseconds");
      }
    }
  }
  return times;
}

function timeOf(
  times: TimeClaim[],
  name: TimeClaim["name"]
): number | undefined {
  return times.find((t) => t.name === name)?.seconds;
}

function checkTimes(
  times: TimeClaim[],
  payload: DataMap,
  warnings: Set<JwtWarning>
): void {
  const exp = timeOf(times, "exp");
  const iat = timeOf(times, "iat");
  const nbf = timeOf(times, "nbf");
  if (!payload.has("exp")) {
    warnings.add("noExpiry");
  }
  if (exp !== undefined && iat !== undefined && exp < iat) {
    warnings.add("expBeforeIat");
  }
  if (exp !== undefined && nbf !== undefined && nbf > exp) {
    warnings.add("nbfAfterExp");
  }
}

function headerWarnings(header: DataMap, warnings: Set<JwtWarning>): string {
  const alg = header.get("alg");
  const algorithm = typeof alg === "string" ? alg : "";
  if (algorithm === "") {
    warnings.add("algMissing");
  } else if (algorithm.toLowerCase() === "none") {
    warnings.add("algNone");
  }
  if (
    header.has("jku") ||
    header.has("x5u") ||
    header.has("jwk") ||
    header.has("x5c")
  ) {
    warnings.add("headerUrl");
  }
  if (header.has("crit")) {
    warnings.add("critical");
  }
  return algorithm;
}

function splitToken(input: string, warnings: Set<JwtWarning>): string[] {
  let text = input.trim();
  if (RE_BEARER.test(text)) {
    text = text.replace(RE_BEARER, "");
    warnings.add("prefixRemoved");
  }
  text = text.replace(RE_QUOTES, "").replace(RE_WHITESPACE, "");
  return text.split(".");
}

interface Segment {
  text: string;
  bytes: Uint8Array;
}

function readSegment(
  name: string,
  raw: string,
  count: number,
  warnings: Set<JwtWarning>
): Segment | JwtResult {
  if (raw.endsWith("=")) {
    warnings.add("padding");
  }
  if (RE_STANDARD.test(raw)) {
    warnings.add("standardBase64");
  }
  const body = raw.replace(RE_PADDING, "");
  if (!RE_BASE64URL.test(body.replaceAll("+", "-").replaceAll("/", "_"))) {
    return fail("characters", name, count);
  }
  const bytes = decodeBase64Url(body);
  if (bytes === null) {
    return fail("badLength", name, count);
  }
  return { text: body, bytes };
}

function isSegment(value: Segment | JwtResult): value is Segment {
  return "bytes" in value;
}

function parseObject(
  name: string,
  bytes: Uint8Array,
  count: number
): { map: DataMap; text: string } | JwtResult {
  const text = utf8(bytes);
  if (text === null) {
    return fail("notUtf8", name, count);
  }
  try {
    const { value } = parseJson(text);
    if (!isMap(value)) {
      return fail("notObject", name, count);
    }
    return { map: value, text };
  } catch (error) {
    if (error instanceof DataError) {
      return fail("notJson", name, count, error.line, error.column);
    }
    return fail("notJson", name, count);
  }
}

function isResult(value: object): value is JwtResult {
  return "ok" in value;
}

/** Decode a token's header and payload. Does not verify anything. */
export function decodeJwt(input: string): JwtResult {
  const warnings = new Set<JwtWarning>();
  if (input.trim() === "") {
    return fail("empty");
  }
  const parts = splitToken(input, warnings);
  if (parts.length !== 3 && parts.length !== 5) {
    return fail("segments", "", parts.length);
  }
  const encrypted = parts.length === 5;
  const headerSegment = readSegment("header", parts[0], parts.length, warnings);
  if (!isSegment(headerSegment)) {
    return headerSegment;
  }
  const header = parseObject("header", headerSegment.bytes, parts.length);
  if (isResult(header)) {
    return header;
  }
  const algorithm = headerWarnings(header.map, warnings);
  if (encrypted) {
    warnings.add("encrypted");
    return {
      ok: true,
      jwt: {
        encrypted,
        headerJson: stringifyJson(header.map, "2", 1_000_000),
        header: header.map,
        payloadJson: "",
        payload: null,
        payloadText: "",
        signature: parts[4],
        signatureBytes: 0,
        algorithm,
        headerClaims: toClaims(header.map, new Set()),
        registered: [],
        other: [],
        times: [],
        warnings: [...warnings],
      },
    };
  }
  return decodeSigned(parts, header.map, algorithm, warnings);
}

function decodeSigned(
  parts: string[],
  header: DataMap,
  algorithm: string,
  warnings: Set<JwtWarning>
): JwtResult {
  const payloadSegment = readSegment(
    "payload",
    parts[1],
    parts.length,
    warnings
  );
  if (!isSegment(payloadSegment)) {
    return payloadSegment;
  }
  const signatureSegment = readSegment(
    "signature",
    parts[2],
    parts.length,
    warnings
  );
  if (!isSegment(signatureSegment)) {
    return signatureSegment;
  }
  const payload = parseObject("payload", payloadSegment.bytes, parts.length);
  let map: DataMap | null = null;
  let payloadText = "";
  if (isResult(payload)) {
    const code = payload.ok ? "" : payload.error.code;
    if (code !== "notObject" && code !== "notJson") {
      return payload;
    }
    payloadText = utf8(payloadSegment.bytes) ?? "";
    warnings.add("payloadNotJson");
  } else {
    map = payload.map;
  }
  const times = map ? readTimes(map, warnings) : [];
  if (map) {
    checkTimes(times, map, warnings);
  }
  if (signatureSegment.bytes.length === 0) {
    warnings.add("unsecured");
  }
  const expected = SIGNATURE_BYTES[algorithm];
  if (
    expected !== undefined &&
    signatureSegment.bytes.length > 0 &&
    signatureSegment.bytes.length !== expected
  ) {
    warnings.add("signatureSize");
  }
  const skip = new Set(REGISTERED);
  return {
    ok: true,
    jwt: {
      encrypted: false,
      headerJson: stringifyJson(header, "2", 1_000_000),
      header,
      payloadJson: map ? stringifyJson(map, "2", 1_000_000) : "",
      payload: map,
      payloadText,
      signature: signatureSegment.text,
      signatureBytes: signatureSegment.bytes.length,
      algorithm,
      headerClaims: toClaims(header, new Set()),
      registered: map
        ? REGISTERED.filter((n) => map.has(n)).map((name) => ({
            name,
            value: map.get(name) as DataValue,
            json: stringifyJson(
              map.get(name) as DataValue,
              "compact",
              1_000_000
            ),
          }))
        : [],
      other: map ? toClaims(map, skip) : [],
      times,
      warnings: [...warnings],
    },
  };
}

/** The expected signature size for algorithms whose size is fixed, else 0. */
export function expectedSignatureBytes(algorithm: string): number {
  return SIGNATURE_BYTES[algorithm] ?? 0;
}

// ---------------------------------------------------------------------------
// Time (the caller supplies "now")
// ---------------------------------------------------------------------------

export type Validity = "expired" | "notYet" | "current" | "unknown";

/** Whether the token's own time claims say it is usable at `nowSeconds`. */
export function validityAt(times: TimeClaim[], nowSeconds: number): Validity {
  const exp = timeOf(times, "exp");
  const nbf = timeOf(times, "nbf");
  if (exp !== undefined && nowSeconds >= exp) {
    return "expired";
  }
  if (nbf !== undefined && nowSeconds < nbf) {
    return "notYet";
  }
  return exp === undefined && nbf === undefined ? "unknown" : "current";
}

export type DurationUnit = "year" | "day" | "hour" | "minute" | "second";

const UNIT_SECONDS: readonly [DurationUnit, number][] = [
  ["year", 31_536_000],
  ["day", 86_400],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/** The largest whole unit that fits: 3600 → { unit: "hour", count: 1 }. */
export function roughDuration(seconds: number): {
  unit: DurationUnit;
  count: number;
} {
  const abs = Math.abs(seconds);
  for (const [unit, size] of UNIT_SECONDS) {
    if (abs >= size) {
      return { unit, count: Math.round(abs / size) };
    }
  }
  return { unit: "second", count: Math.round(abs) };
}
